import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API, formatApiErrorDetail } from "../lib/api";

function Shell({ title, children }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen grain relative flex items-center justify-center px-4">
      <div className="w-full max-w-md relative z-[2]">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: "#0A3F4A" }}>
            <img src="/cen21-logo.webp" alt="Cen21" className="w-full h-full object-contain" style={{ transform: "scale(2.4)" }} />
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("app_name")}</div>
        </div>
        <div className="glass rounded-3xl p-8">
          <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>{title}</h1>
          {children}
          <div className="mt-6 text-sm text-center">
            <Link data-testid="back-to-login" to="/login" className="text-indigo-400 hover:text-indigo-300">{t("login")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setSent(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title={t("forgot_password")}>
      {sent ? (
        <p data-testid="forgot-sent-msg" className="text-sm text-emerald-300">{t("forgot_sent")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-400">{t("forgot_hint")}</p>
          <div>
            <Label className="text-slate-300">{t("email")}</Label>
            <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
          <Button type="submit" data-testid="forgot-submit-btn" disabled={loading} className="w-full btn-glow bg-indigo-500 hover:bg-indigo-600">{loading ? "…" : t("send_link")}</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error(t("password_mismatch")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      toast.success(t("password_reset_ok"));
      nav("/login");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title={t("reset_password")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-slate-300">{t("new_password")}</Label>
          <Input data-testid="reset-password-input" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-slate-300">{t("confirm_password")}</Label>
          <Input data-testid="reset-password-confirm-input" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
        </div>
        <Button type="submit" data-testid="reset-submit-btn" disabled={loading || !token} className="w-full btn-glow bg-indigo-500 hover:bg-indigo-600">{loading ? "…" : t("save")}</Button>
      </form>
    </Shell>
  );
}
